import { useCallback, useEffect, useState, type ReactNode } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import { Paginazione, Riquadro, Tabella, totaleDi, type ConTotale } from './base.tsx'
import { Attesa, Avviso, Intestazione, Vuoto } from './Pezzi.tsx'

/** Aiuti comuni alle pagine di elenco: lettura a finestre, conteggio, impaginazione. */

/** Righe per pagina. Resta entro il massimo accettato dalle rotte di elenco. */
export const LIMITE = 50

/** Finestra di un elenco: posizione, totale noto e comando di spostamento. */
export type Finestra = { salta: number; totale: number | null; cambia: (salta: number) => void }

/**
 * Elenco letto a finestre.
 *
 * Il percorso passato non contiene `limite` e `salta`: li aggiunge questo gancio, cosi' la
 * finestra resta un dato di un punto solo. Il totale sta sulla colonna `totale` di ogni riga;
 * finche' una rotta non la espone vale null e la navigazione si regola sulle righe ricevute.
 */
export function usaElenco<T>(percorso: string) {
  const [dati, setDati] = useState<(T & ConTotale)[] | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [salta, setSalta] = useState(0)
  // L'ultima pagina di un elenco puo' tornare vuota, e una pagina vuota non porta la colonna
  // `totale`. Senza memoria il conteggio passerebbe da «di 128» a nulla proprio nel punto in
  // cui serve per tornare indietro, quindi si conserva l'ultimo valore noto.
  const [totaleNoto, setTotaleNoto] = useState<number | null>(null)

  // Il percorso porta i filtri. Quando cambiano la finestra riparte da capo: la pagina
  // calcolata sul filtro precedente non corrisponde piu' ad alcun risultato. L'assestamento
  // avviene in fase di render, quindi la lettura parte una volta sola e con la finestra nuova.
  const [percorsoInUso, setPercorsoInUso] = useState(percorso)
  if (percorso !== percorsoInUso) {
    setPercorsoInUso(percorso)
    setSalta(0)
    setTotaleNoto(null)
  }

  const ricarica = useCallback(() => {
    setDati(null)
    const separatore = percorso.includes('?') ? '&' : '?'
    api.get<(T & ConTotale)[]>(`${percorso}${separatore}limite=${LIMITE}&salta=${salta}`)
      .then((righe) => {
        setDati(righe)
        const letto = totaleDi(righe)
        if (letto !== null) setTotaleNoto(letto)
        // La prima pagina vuota vuol dire nessuna riga: per esempio dopo aver eliminato l'ultima.
        else if (salta === 0 && !righe.length) setTotaleNoto(0)
      })
      .catch((e) => setErrore(e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.'))
  }, [percorso, salta])
  useEffect(ricarica, [ricarica])

  const finestra: Finestra = { salta, totale: totaleDi(dati) ?? totaleNoto, cambia: setSalta }
  return { dati, errore, ricarica, finestra }
}

/**
 * Conteggio complessivo per il sottotitolo.
 *
 * Con la finestra attiva il numero di righe ricevute non e' il numero di voci esistenti:
 * il sottotitolo resta assente finche' la rotta non espone il totale.
 */
export function conteggio(totale: number | null, singolare: string, plurale: string) {
  if (totale === null) return undefined
  return `${totale} ${totale === 1 ? singolare : plurale}`
}

/** Impaginazione comune degli elenchi: intestazione, stato di attesa, errore, tabella. */
export function Guscio({
  titolo, sottotitolo, azioni, errore, dati, vuoto, intestazioni, finestra, children,
}: {
  titolo: string; sottotitolo?: ReactNode; azioni?: ReactNode; errore: string | null
  dati: unknown[] | null; vuoto: string; intestazioni: ReactNode[]
  /** La navigazione sta dentro il riquadro, sotto la tabella, anche quando la pagina e' vuota. */
  finestra: Finestra
  children: ReactNode
}) {
  return (
    <>
      <Intestazione titolo={titolo} sottotitolo={sottotitolo} azioni={azioni} />
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!errore && !dati && <Attesa testo="Caricamento…" />}
      {dati && (
        <Riquadro denso>
          <Tabella intestazioni={intestazioni} vuoto={<Vuoto testo={vuoto} />}>
            {children}
          </Tabella>
          <Paginazione salta={finestra.salta} limite={LIMITE} totale={finestra.totale}
                       ricevute={dati.length} cambia={finestra.cambia} />
        </Riquadro>
      )}
    </>
  )
}

export const giorno = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('it-IT') : '—')

/** Messaggio unico quando la causa dell'errore non e' nota. */
export const NON_RIUSCITA = 'Non è andata a buon fine: controlla la connessione e riprova.'

/** Testo da mostrare per un errore dell'API: il suo messaggio, altrimenti quello generico. */
export const messaggioErrore = (e: unknown) => (e instanceof ErroreApi ? e.message : NON_RIUSCITA)
